import { Button, useTranslation } from '@hbs-us/ui';
import { useEffect, useState } from 'react';

import { useAccess } from '../../../auth/useAccess.js';
import { useCompanySettingsMutation, useSettingsQuery } from '../useSettingsQuery.js';

/**
 * Whether a crew member may move a job step back (`crewCanStepBack`, default on), for when they tap
 * the wrong step. The office still sees every change. Only an owner changes it; anyone else who can
 * open Settings reads it. The card hides until the server sends the key, because the server refuses
 * a key it does not know.
 *
 * It saves on its own rather than through the page's Save bar: company rules are owner-only on
 * the server, and the page's Save is open to anyone with settings.manage.
 */
export function CrewStepBackCard() {
  const { t } = useTranslation();
  const canEdit = useAccess()({ minRole: 'Admin' });
  const stored = useSettingsQuery().data?.companySettings?.crewCanStepBack;
  const save = useCompanySettingsMutation();
  const [allowed, setAllowed] = useState(stored === true);

  useEffect(() => {
    if (typeof stored === 'boolean') setAllowed(stored);
  }, [stored]);

  if (typeof stored !== 'boolean') return null;

  return (
    <section className="rounded-md border border-border bg-card p-6">
      <h2 className="mb-1 font-display font-semibold text-foreground text-lg">
        {t('settings:crewStepBack.heading')}
      </h2>
      <p className="mb-4 text-muted-foreground text-sm">
        {canEdit ? t('settings:crewStepBack.description') : t('settings:crewStepBack.ownersOnly')}
      </p>
      <label className="flex min-h-11 items-start gap-3">
        <input
          type="checkbox"
          className="mt-0.5 size-5 shrink-0 accent-primary disabled:cursor-not-allowed"
          checked={allowed}
          disabled={!canEdit}
          onChange={(e) => setAllowed(e.target.checked)}
        />
        <span>
          <span className="font-medium text-foreground text-sm">
            {t('settings:crewStepBack.label')}
          </span>
          <span className="mt-1 block text-muted-foreground text-xs">
            {t('settings:crewStepBack.hint')}
          </span>
        </span>
      </label>
      {canEdit ? (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
          {save.isError ? (
            <p role="alert" className="mr-auto text-destructive text-sm">
              {t('settings:crewStepBack.saveError')}
            </p>
          ) : null}
          {save.isSuccess ? (
            <p role="status" className="mr-auto text-muted-foreground text-sm">
              {t('settings:crewStepBack.saved')}
            </p>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            disabled={save.isPending || allowed === stored}
            onClick={() => save.mutate({ crewCanStepBack: allowed })}
          >
            {t('settings:crewStepBack.save')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
